import { useState } from "react";
import styles from "./ReportsList.module.scss";
import { Button, Modal, Table, TablePaginationConfig, Tooltip, Typography } from "antd";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import Layout from "../../Containers/Layout";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowLeftOutlined, InboxOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { theme } from "../../theme";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import { getReportsByProgramId, deleteReport } from "../../Requests/base-report-requests";
import { downloadFile } from "../../utils/downloadUtils";
import { downloadReportFile, uploadReportFile } from "../../Requests/file-manager-requests";
import { dateFormat } from "../../utils/constants";
import moment from "moment";
import Dragger from "antd/lib/upload/Dragger";
import { ReportType } from "../../Api";

const ReportsList = () => {
    const { t } = useTranslation();
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrent] = useState(1);


    const { programId, reportType, studyCycle } = useParams<{
        programId: string;
        reportType: ReportType;
        studyCycle: string;
    }>();


    // Upload modal
    const uploadSucceded = () => {
        handleClose();
        openNotification("Succes", "Încarcarea s-a realizat cu succes!", NOTIFICATION_TYPES.SUCCESS);
    };

    const uploadFailed = (_err: any) => {
        openNotification("Eroare", "Încarcarea nu s-a realizat cu succes!", NOTIFICATION_TYPES.ERROR);
    };

    const wrongFile = () => {
        openNotification("Eroare", "Extensia fișierului selectat nu este permisă!", NOTIFICATION_TYPES.ERROR);
    };

    const upload = async (file: Blob | null) => {
        if (file) {
            await modalProps.uploadHandler(modalProps.reportId, reportType, file).then(uploadSucceded);
        }
    };

    const [modalProps, setModalProps] = useState({
        isModalVisible: false,
        title: "",
        fileExtension: "",
        reportId: "",
        uploadHandler: uploadReportFile,
    });

    const handleShow = (id: string, uploadHandler: any, fileExtension: string = "pdf") => {
        setModalProps((modalProps) => ({
            ...modalProps,
            isModalVisible: true,
            id,
            uploadHandler,
            fileExtension,
            reportId: id,
        }));
    };

    const handleClose = () => {
        setModalProps((modalProps) => ({ ...modalProps, isModalVisible: false }));
    };

    const handleOk = () => {
        setModalProps((modalProps) => ({ ...modalProps, isModalVisible: false }));
    };

    // Delete confirmation modal
    const [modalPropsDelete, setModalPropsDelete] = useState({
        isModalVisible: false,
        title: "",
        id: "",
    });

    const handleShowDelete = (id: string) => {
        setModalPropsDelete((modalPropsDelete) => ({ ...modalPropsDelete, isModalVisible: true, id }));
    };

    const handleCloseDelete = () => {
        setModalPropsDelete((modalPropsDelete) => ({ ...modalPropsDelete, isModalVisible: false }));
    };

    const handleOkDelete = () => {
        setModalPropsDelete((modalPropsDelete) => ({ ...modalPropsDelete, isModalVisible: false }));
        deleteReport(modalPropsDelete.id)
            .then(() => {
                deleteSucceded();
                invalidateQuery();
            })
            .catch(deleteFailed);
    };

    const deleteSucceded = () => {
        openNotification("Succes", "Ștergerea s-a realizat cu succes!", NOTIFICATION_TYPES.SUCCESS);
    };

    const deleteFailed = (_err: any) => {
        openNotification("Eroare", "Ștergerea nu s-a realizat cu succes!", NOTIFICATION_TYPES.ERROR);
    };

    const queryClient = useQueryClient();

    const invalidateQuery = () => {
        queryClient.invalidateQueries(["getReports", reportType, programId]);
    };

    const openFetchError = (_error: any) => {
        openNotification("Eroare!", "Datele nu au putut fi aduse din server!", NOTIFICATION_TYPES.ERROR);
    };

    const { data, isLoading } = useQuery(
        ["getReports", reportType, programId, pageSize, currentPage],
        () => getReportsByProgramId(programId, reportType as any, currentPage, pageSize),
        {
            onError: openFetchError,
        }
    );

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrent(() => pagination.current ?? 1);
        setPageSize(() => pagination.pageSize ?? 10);
    };

    const reportTypeTranslations: Record<ReportType, string> = {
        ExternalEvaluation: "Raport de evaluare externă",
        AdminBoardDecision: "Hotărâri Consiliu de Administrație",
        SenateDecision: "Hotărâri Senat",
        SustainabilityAnalysis: "Analiză de sustenabilitate",
        StakeholderConsultationProof: "Dovadă consultare părți interesate",
        RNCISForm: "Formular RNCIS",
        RNCISCertificate: "Certificat RNCIS",
        DiplomaSupplement: "Supliment diplomă",
        InternalEvaluationReport: "Raport de evaluare internă",
        ProgressReport: "Raport de progres",
        OtherRelevantDocuments: "Alte documente relevante",
        BaseReport: "Raport de bază",
    };

    const translatedReportType = reportTypeTranslations[reportType];

    const reportTypeTranslationsDownload: Record<ReportType, string> = {
        ExternalEvaluation: "Raport de evaluare externă",
        AdminBoardDecision: "Hotărâre Consiliu de Administrație",
        SenateDecision: "Hotărâre Senat",
        SustainabilityAnalysis: "Analiză de sustenabilitate",
        StakeholderConsultationProof: "Dovadă consultare parte interesată",
        RNCISForm: "Formular RNCIS",
        RNCISCertificate: "Certificat RNCIS",
        DiplomaSupplement: "Supliment diplomă",
        InternalEvaluationReport: "Raport de evaluare internă",
        ProgressReport: "Raport de progres",
        OtherRelevantDocuments: "Alt document relevant",
        BaseReport: "Raport de bază",
    };

    const translatedReportTypeDownload = reportTypeTranslationsDownload[reportType];


    const columns = [
        {
            title: "Data raportului",
            dataIndex: "reportDate",
            width: "130px",
            key: "reportDate",
            render: (date: Date) => moment(date).format(dateFormat),
        },
        {
            title: "Descriere",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "Acțiuni",
            width: "200px",
            render: (text: string, record: any) => (
                <div>
                    <Tooltip title="Descărcare">
                        <FontAwesomeIcon
                            icon={solid("download")}
                            style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                            onClick={() =>
                                downloadFile(
                                    downloadReportFile(record.id, reportType),
                                    `${translatedReportTypeDownload} din ${moment(record.reportDate).format(dateFormat)}`
                                )
                            }
                        />
                    </Tooltip>
                    <Tooltip title="Încărcare">
                        <FontAwesomeIcon
                            icon={solid("upload")}
                            style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                            onClick={() => handleShow(record.id!, uploadReportFile, "pdf")}
                        />
                    </Tooltip>
                    <Link to={`/${studyCycle}/editare-raport/${reportType}/${programId}/${record.id}`}>
                        <Tooltip title="Editare">
                            <FontAwesomeIcon
                                icon={solid("edit")}
                                style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                            />
                        </Tooltip>
                    </Link>
                    <Tooltip title="Ștergere">
                        <FontAwesomeIcon
                            icon={solid("trash")}
                            style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                            onClick={() => handleShowDelete(record.id)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
            <Layout>
                <Modal
                    open={modalPropsDelete.isModalVisible}
                    onOk={handleOkDelete}
                    onCancel={handleCloseDelete}
                    title="Ștergere"
                    width={"400px"}
                    okText={t("account.confirm")}
                    cancelText={t("account.cancel")}
                    cancelButtonProps={{
                        style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" },
                    }}
                    okButtonProps={{
                        style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" },
                    }}
                >
                    <div>
                        <Typography>Sigur doriti stergerea raportului?</Typography>
                    </div>
                </Modal>
                <Modal
                    open={modalProps.isModalVisible}
                    closable={false}
                    onOk={handleOk}
                    onCancel={handleClose}
                    title="Încărcare fișier"
                    width={"50%"}
                    cancelText="Anulare"
                    cancelButtonProps={{
                        style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" },
                    }}
                    okButtonProps={{ style: { display: "none" } }}
                >
                    <div>
                        <Dragger
                            multiple={false}
                            beforeUpload={(uploaded) => {
                                if (uploaded.name.includes(modalProps.fileExtension)) {
                                    upload(uploaded).then(invalidateQuery).catch(uploadFailed);
                                } else {
                                    wrongFile();
                                }
                                return false;
                            }}
                            showUploadList={false}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: theme.green }} />
                            </p>
                            <p className="ant-upload-text">
                                Dă click aici sau trage fișierul pe care vrei să îl încarci în acest chenar
                            </p>
                            <p className="ant-upload-hint">Extensii acceptate: {modalProps.fileExtension}</p>
                        </Dragger>
                    </div>
                </Modal>
                
                    <h2>{translatedReportType}</h2>
                    
                <Table
                    className={styles.usersTable}
                    dataSource={data?.data || []}
                    pagination={{
                        total: data?.totalCount,
                        current: currentPage,
                        pageSize: data?.pageSize,
                        pageSizeOptions: ["10", "20", "50"],
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total: number, range: [number, number]) => (
                            <div>
                                <Tooltip title="Înapoi">
                                    <Link to={`/${studyCycle}/programe`}>
                                        <Button type="primary" icon={<ArrowLeftOutlined />} style={{ marginRight: 10 }}>
                                            Înapoi
                                        </Button>
                                    </Link>
                                </Tooltip>
                                <Tooltip title={'Adaugă'}>
                                    <Link to={`/${studyCycle}/adaugare-raport/${reportType}/${programId}`}>
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            Adaugă
                                        </Button>
                                    </Link>
                                </Tooltip>
                            </div>
                        ),
                    }}
                    locale={{
                        emptyText: (
                            <span>
                                <h3>
                                    Nu există {translatedReportType} adăugat! Apasă butonul de mai jos pentru a adăuga un nou
                                    raport!
                                </h3>
                                <Tooltip title={'Adaugă'}>
                                    <Link to={`/${studyCycle}/adaugare-raport/${reportType}/${programId}`}>
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            Adaugă
                                        </Button>
                                    </Link>
                                </Tooltip>
                                <br />
                                <br />
                                <Tooltip title="Înapoi">
                                    <Link to={`/${studyCycle}/programe`}>
                                        <Button type="primary" icon={<ArrowLeftOutlined />} style={{ marginRight: 10 }}>
                                            Înapoi
                                        </Button>
                                    </Link>
                                </Tooltip>
                            </span>
                        ),
                    }}
                    columns={columns}
                    rowKey={(record) => record.id ?? ""}
                    loading={isLoading}
                    onChange={handleTableChange}
                />
            </Layout>
        </div>
    );
};

export default ReportsList;